import { HttpClient, HttpEvent, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Inject, Injectable, NgZone, OnDestroy, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
    Observable,
    Subject,
    Subscription,
    catchError,
    filter,
    map,
    of,
    share,
    switchMap,
    takeUntil,
    takeWhile,
    tap,
    timer
} from 'rxjs';

import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { WINDOW } from 'src/app/core/core.module';
import { ENV, Environment } from 'src/environments/environment.provider';

export interface AppVersion {
    major: number;
    minor: number;
    build: string | number;
    revision?: number;
}

/**
 * Transforms a string in the form of `#.#.#-####` to the AppVersion interface.
 *
 * Example: `parseAppVersion(1.2.3-4567)`
 *
 * output: `{major: 1, minor:2, build:3, revision:4567}`
 */
export function parseAppVersion(appVer: string): AppVersion {
    const v = appVer.split('.');
    const major = +v[0];
    const minor = +v[1];
    const buildRev = v[2].split('-');
    const revision = +buildRev[0];
    // handle default version number without revision `1.0.0`
    const build = buildRev.length > 1 ? +buildRev[1] : 0;

    return {
        major,
        minor,
        revision,
        build
    };
}

/**
 * Formats the appVersion parts to a typical version format `#.#.#-####`
 * The build is optional, and if not provided, will be separated by a hyphen for allowing easy string replacement.
 * But we need to include the build for datadogRum to pick up our source maps correctly after they are uploaded.
 * @returns string
 */
export function formatAppVersion(appVersion: AppVersion, includeBuild = false): string {
    return includeBuild
        ? _formatAppVersionWithRevisionAndBuild(appVersion)
        : _formatAppVersionWithoutRevision(appVersion);
}

// The build number is chopped off from this string for showing in the UI
function _formatAppVersionWithoutRevision(appVersion: AppVersion): string {
    return `${appVersion.major}.${appVersion.minor}.${appVersion.revision}-${appVersion.build}`;
}

function _formatAppVersionWithRevisionAndBuild(appVersion: AppVersion): string {
    return `${appVersion.major}.${appVersion.minor}.${appVersion.revision}.${appVersion.build}`;
}

/**
 * Given the `environment` file has an `appVersion` property setup to extract the
 * `version` property of `package.json`, this will perform an http get every 5
 * minutes for the `app-version.json` currently on the server hosting the app
 * and compare to the version in the `package.json` file cached in the browser
 */
@Injectable({
    providedIn: 'root'
})
export class VersionCheckerService implements OnDestroy {
    newVersion = false;
    currentAppVersion: AppVersion = undefined;
    subscriptions: Subscription = Subscription.EMPTY;
    readonly timedVersionPoll = timer(this.settings.versionPollInterval, this.settings.versionPollInterval).pipe(
        switchMap(() => this.getAndNotifyOnNew()),
        // Prevent multiple timers making the same call
        share()
    );

    readonly newVersionSignal = signal<boolean>(false);

    private readonly _cleanup$ = new Subject<void>();

    constructor(
        @Inject(APP_CONFIG) readonly settings: Settings,
        @Inject(ENV) readonly env: Environment,
        @Inject(WINDOW) private readonly window: Window,
        private readonly http: HttpClient,
        private readonly ngZone: NgZone,
        private readonly _router: Router
    ) {
        this.currentAppVersion = this.env.appVersion;

        this._router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .pipe(takeUntil(this._cleanup$))
            .subscribe(() => {
                if (this.newVersionSignal()) {
                    this.window.location.reload();
                }
            });
    }

    ngOnDestroy(): void {
        this._cleanup$.next();
        this._cleanup$.complete();
    }

    public startPollingVersion() {
        this.timedVersionPoll.subscribe();
    }

    private getAndNotifyOnNew(): Observable<{ newVersion: boolean; appVer: AppVersion }> {
        return this.getAndCompareVersion().pipe(
            tap(({ appVer }) => {
                if (this.newVersion) {
                    this.showNewVersionNotice(appVer);
                }

                this.newVersionSignal.set(this.newVersion);
            })
        );
    }

    private getAndCompareVersion(): Observable<{ newVersion: boolean; appVer: AppVersion }> {
        return of(this.currentAppVersion).pipe(
            takeWhile(() => !this.newVersion),
            switchMap((currentAppVersion) =>
                this.getAppVersion().pipe(catchError((_) => of(new HttpResponse({ body: currentAppVersion }))))
            ),
            map((response: HttpResponse<AppVersion>) => response.body),
            map((appVer: AppVersion) => {
                this.newVersion = JSON.stringify(this.currentAppVersion) !== JSON.stringify(appVer);
                return { newVersion: this.newVersion, appVer };
            })
        );
    }

    private showNewVersionNotice(appVer: AppVersion) {
        // tslint:disable-next-line:no-console
        console.info(
            `current version: ${JSON.stringify(this.currentAppVersion)}, new version available: ${JSON.stringify(
                appVer
            )}`
        );
    }

    private getAppVersion(): Observable<HttpEvent<AppVersion>> {
        const options: any = {
            observe: 'response',
            responseType: 'json',
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                Accept: 'application/json'
            })
        };
        return this.ngZone.run(() => {
            return this.http.get<AppVersion>('/app-version.json', options);
        });
    }
}
